import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  AlertTriangle, Target, TrendingUp, TrendingDown, FileText,
  ArrowUpRight, ArrowDownRight, Shield, BarChart3, Gem, Factory, Activity, AlertCircle, Eye
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import PageHeader from '../../components/PageHeader';
import KPICardOperations from '../../components/KPICardOperations';
import RealVsBudgetTrajectory from '../../components/RealVsBudgetTrajectory';
import { riskDeliveryTrajectoryKpis, riskDeliveryData } from '../../data/mock/operationsData';
import { processKeyMessages } from '../../utils/iconMapping';

// ============ COMPONENTS ============

// Severity Badge Component
const SeverityBadge = ({ severity, language }) => {
  const configs = {
    'high': { bg: 'bg-rose-100', text: 'text-rose-700', label: { pt: 'Alto', en: 'High' } },
    'medium': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Médio', en: 'Medium' } },
    'low': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Baixo', en: 'Low' } },
  };
  const config = configs[severity] || configs['medium'];
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// ============ MAIN COMPONENT ============
const RiskDelivery = ({ year, setActiveSection, flowMap }) => {
  const { language } = useLanguage();
  const is2026 = year === 2026;
  const rawData = is2026 ? riskDeliveryData[2026] : riskDeliveryData[2025];
  
  // Process data: map icon keys to actual icon components
  const data = {
    ...rawData,
    keyMessages: processKeyMessages(rawData.keyMessages),
  };

  // Dados para o painel Real vs Trajectória do Orçamento - array completo de KPIs da página
  const trajectoryKpisYear = riskDeliveryTrajectoryKpis[year] || [];

  // Preparar dados para export PDF
  const pageDataForExport = {
    kpis: data.kpis,
    kpiLabels: {
      quilatesRecuperados: language === 'pt' ? 'Quilates Recuperados' : 'Recovered Carats',
      volumeTotalDMS: language === 'pt' ? 'Volume Total DMS' : 'Total DMS Volume',
      gapQuilatesVsOrc: language === 'pt' ? 'Gap Quilates vs Orç.' : 'Carats Gap vs Budget',
      volumeNaoProdutivo: language === 'pt' ? 'Volume Não Produtivo' : 'Non-Productive Volume',
    },
    keyMessages: data.keyMessages,
    desviosMateriais: data.desviosMateriais,
    fragilidades: data.fragilidades,
    watchlist: data.watchlist,
    ponteRisco: data.ponteRisco,
  };

  return (
    <div className="space-y-6" data-testid="risk-delivery">
      {/* Page Header */}
      <PageHeader
        title={language === 'pt' ? 'Risco e Entrega do Plano' : 'Risk & Plan Delivery'}
        period={data.period}
        subtitle={language === 'pt' 
          ? `Sinais de risco e execução do plano operacional`
          : `Risk signals and operational plan execution`}
        language={language}
        flowMap={flowMap}
        pageData={pageDataForExport}
      />

      {/* ============ MENSAGENS-CHAVE - McKinsey Premium Compact ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#112240] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-0.5 h-4 bg-gold-500 rounded-full"></div>
          <h3 className="text-white/90 font-medium text-xs uppercase tracking-[0.15em]">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {data.keyMessages.map((msg, idx) => {
            const MsgIcon = msg.icon;
            return (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/8 transition-colors">
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.iconBg}`}>
                    <MsgIcon className={`w-4 h-4 ${msg.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-[10px] mb-1.5 uppercase tracking-wider ${msg.titleColor}`}>
                      {msg.title[language]}
                    </h4>
                    <p className="text-slate-300/90 text-[11px] leading-relaxed">{msg.text[language]}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ 4 KPIs OFICIAIS ============ */}
      <div className="grid grid-cols-4 gap-4">
        <KPICardOperations 
          label={language === 'pt' ? 'Quilates Recuperados' : 'Recovered Carats'}
          value={(data.kpis.quilatesRecuperados.actual / 1000).toFixed(1) + 'K'}
          unit={data.kpis.quilatesRecuperados.unit}
          vsBudget={data.kpis.quilatesRecuperados.variance}
          vsPrior={data.kpis.quilatesRecuperados.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Quilates Recuperados' : 'Recovered Carats',
            definition: language === 'pt' 
              ? 'Total de diamantes recuperados da planta de processamento no período.'
              : 'Total diamonds recovered from the processing plant in the period.',
            relevance: language === 'pt'
              ? 'Indicador final de output operacional. Base para cálculo de receita e entrega ao mercado.'
              : 'Final operational output indicator. Base for revenue calculation and market delivery.',
            currentReading: language === 'pt'
              ? `${(data.kpis.quilatesRecuperados.actual / 1000).toFixed(1)}K quilates recuperados (${data.kpis.quilatesRecuperados.variance > 0 ? '+' : ''}${data.kpis.quilatesRecuperados.variance}% vs orçamento).`
              : `${(data.kpis.quilatesRecuperados.actual / 1000).toFixed(1)}K carats recovered (${data.kpis.quilatesRecuperados.variance > 0 ? '+' : ''}${data.kpis.quilatesRecuperados.variance}% vs budget).`,
            drillDown: [
              { label: language === 'pt' ? 'Jan' : 'Jan', value: '28.5K ct', variance: -4.2 },
              { label: language === 'pt' ? 'Fev' : 'Feb', value: '26.8K ct', variance: -6.8 },
              { label: language === 'pt' ? 'Mar' : 'Mar', value: '29.2K ct', variance: -5.1 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Volume Total DMS' : 'Total DMS Volume'}
          value={data.kpis.volumeTotalDMS.actual}
          unit={data.kpis.volumeTotalDMS.unit}
          vsBudget={data.kpis.volumeTotalDMS.variance}
          vsPrior={data.kpis.volumeTotalDMS.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Volume Total DMS' : 'Total DMS Volume',
            definition: language === 'pt' 
              ? 'Total de material processado pelo Dense Media Separator (separação por densidade).'
              : 'Total material processed by the Dense Media Separator (density separation).',
            relevance: language === 'pt'
              ? 'Indicador de capacidade de concentração. Volume DMS abaixo do plano limita recuperação potencial.'
              : 'Concentration capacity indicator. DMS volume below plan limits potential recovery.',
            currentReading: language === 'pt'
              ? `${data.kpis.volumeTotalDMS.actual} Kt processadas no DMS (${data.kpis.volumeTotalDMS.variance > 0 ? '+' : ''}${data.kpis.volumeTotalDMS.variance}% vs orçamento).`
              : `${data.kpis.volumeTotalDMS.actual} Kt processed in DMS (${data.kpis.volumeTotalDMS.variance > 0 ? '+' : ''}${data.kpis.volumeTotalDMS.variance}% vs budget).`,
            drillDown: [
              { label: 'DMS Linha 1', value: '485 Kt', variance: 2.1 },
              { label: 'DMS Linha 2', value: '407 Kt', variance: -5.8 },
              { label: language === 'pt' ? 'Total Acumulado' : 'Total Cumulative', value: '892 Kt' },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Gap Quilates vs Orç.' : 'Carats Gap vs Budget'}
          value={data.kpis.gapQuilatesVsOrc.actual > 0 ? '+' + data.kpis.gapQuilatesVsOrc.actual : data.kpis.gapQuilatesVsOrc.actual}
          unit={data.kpis.gapQuilatesVsOrc.unit}
          vsBudget={data.kpis.gapQuilatesVsOrc.variance}
          vsPrior={data.kpis.gapQuilatesVsOrc.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Gap Quilates vs Orçamento' : 'Carats Gap vs Budget',
            definition: language === 'pt' 
              ? 'Desvio absoluto entre quilates recuperados e o orçamento para o período.'
              : 'Absolute deviation between recovered carats and budget for the period.',
            relevance: language === 'pt'
              ? 'Principal indicador de risco de entrega. Gap negativo implica necessidade de recuperação nos trimestres seguintes.'
              : 'Main delivery risk indicator. Negative gap implies need for recovery in following quarters.',
            currentReading: language === 'pt'
              ? `Gap de ${data.kpis.gapQuilatesVsOrc.actual} kcts representa ${Math.abs(data.kpis.gapQuilatesVsOrc.actual / 85 * 100).toFixed(1)}% do orçamento trimestral.`
              : `Gap of ${data.kpis.gapQuilatesVsOrc.actual} kcts represents ${Math.abs(data.kpis.gapQuilatesVsOrc.actual / 85 * 100).toFixed(1)}% of quarterly budget.`,
            drillDown: [
              { label: language === 'pt' ? 'Gap Volume' : 'Volume Gap', value: '-1.2K ct', variance: -40 },
              { label: language === 'pt' ? 'Gap Teor' : 'Grade Gap', value: '-0.8K ct', variance: -27 },
              { label: language === 'pt' ? 'Gap Recuperação' : 'Recovery Gap', value: '-0.4K ct', variance: -13 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Volume Não Produtivo' : 'Non-Productive Volume'}
          value={data.kpis.volumeNaoProdutivo.actual}
          unit={data.kpis.volumeNaoProdutivo.unit}
          vsBudget={data.kpis.volumeNaoProdutivo.variance}
          vsPrior={data.kpis.volumeNaoProdutivo.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          inverse={true}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Volume Não Produtivo' : 'Non-Productive Volume',
            definition: language === 'pt' 
              ? 'Material movido que não resultou em minério tratável (estéril em excesso, rehandling).'
              : 'Material moved that did not result in treatable ore (excess waste, rehandling).',
            relevance: language === 'pt'
              ? 'Indicador de ineficiência operacional. Volume não produtivo elevado aumenta custo por quilate.'
              : 'Operational inefficiency indicator. High non-productive volume increases cost per carat.',
            currentReading: language === 'pt'
              ? `${data.kpis.volumeNaoProdutivo.actual} Kt de volume não produtivo (${data.kpis.volumeNaoProdutivo.variance > 0 ? '+' : ''}${data.kpis.volumeNaoProdutivo.variance}% vs orçamento).`
              : `${data.kpis.volumeNaoProdutivo.actual} Kt non-productive volume (${data.kpis.volumeNaoProdutivo.variance > 0 ? '+' : ''}${data.kpis.volumeNaoProdutivo.variance}% vs budget).`,
            drillDown: [
              { label: language === 'pt' ? 'Estéril Excedente' : 'Excess Waste', value: '95 Kt', variance: 8.5 },
              { label: language === 'pt' ? 'Rehandling' : 'Rehandling', value: '42 Kt', variance: 15.2 },
              { label: language === 'pt' ? 'Diluição' : 'Dilution', value: '19 Kt', variance: 5.8 },
            ]
          }}
        />
      </div>

      {/* ============ REAL VS TRAJECTÓRIA DO ORÇAMENTO ============ */}
      {trajectoryKpisYear.length > 0 && (
        <RealVsBudgetTrajectory 
          year={year}
          kpis={trajectoryKpisYear}
          defaultKpi="quilatesRecuperados"
        />
      )}

      {/* ============ SUMÁRIO EXECUTIVO ============ */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Risco e entrega do plano' : 'Risk and plan delivery'}
        language={language}
        maxHeight="500px"
        icon={AlertTriangle}
        dataTestId="risk-delivery-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Esta página acompanha os sinais mais relevantes de risco sobre a entrega do plano. A leitura deve integrar volume, conversão, disciplina operacional e desvios críticos. O CFO deve perceber rapidamente se o plano está a ser executado com robustez ou apenas cumprido de forma aparente e frágil.`
              : `This page tracks the most relevant risk signals on plan delivery. The reading should integrate volume, conversion, operational discipline and critical deviations. The CFO should quickly understand if the plan is being executed with robustness or just being met in an apparent and fragile way.`}
          </p>
          <p className="text-slate-700 leading-relaxed text-sm">
            {language === 'pt' 
              ? `O ${data.period} mostra quilates recuperados ${data.kpis.quilatesRecuperados.variance > 0 ? '+' : ''}${data.kpis.quilatesRecuperados.variance}% vs plano, volume DMS ${data.kpis.volumeTotalDMS.variance > 0 ? '+' : ''}${data.kpis.volumeTotalDMS.variance}% vs plano e volume não produtivo ${data.kpis.volumeNaoProdutivo.variance > 0 ? '+' : ''}${data.kpis.volumeNaoProdutivo.variance}% vs plano. A combinação de desvios em produção, DMS e volumes não produtivos deve ser lida como sinal de pressão sobre previsibilidade operacional.`
              : `${data.period} shows recovered carats ${data.kpis.quilatesRecuperados.variance > 0 ? '+' : ''}${data.kpis.quilatesRecuperados.variance}% vs plan, DMS volume ${data.kpis.volumeTotalDMS.variance > 0 ? '+' : ''}${data.kpis.volumeTotalDMS.variance}% vs plan and non-productive volume ${data.kpis.volumeNaoProdutivo.variance > 0 ? '+' : ''}${data.kpis.volumeNaoProdutivo.variance}% vs plan. The combination of deviations in production, DMS and non-productive volumes should be read as a signal of pressure on operational predictability.`}
          </p>
        </div>

        {/* Driver Sections */}
        <div className="space-y-4">
          {/* Sinais de Entrega */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Sinais de Entrega' : 'Delivery Signals'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Estamos a entregar o plano com consistência?' : 'Are we delivering the plan consistently?'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed">{data.sinaisEntrega.leituraExecutiva[language]}</p>
          </div>

          {/* Fragilidades - only for 2026 */}
          {is2026 && data.fragilidades && (
            <div className="bg-gradient-to-r from-rose-50 to-slate-50 rounded-xl p-5 border border-rose-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Fragilidades Identificadas' : 'Identified Fragilities'}</h4>
                  <p className="text-xs text-slate-500">{language === 'pt' ? 'O plano está a ser entregue com robustez?' : 'Is the plan being delivered with robustness?'}</p>
                </div>
              </div>
              <p className="text-slate-600 text-xs leading-relaxed">
                {language === 'pt' 
                  ? `Foram identificados ${data.fragilidades.length} sinais de fragilidade no período: dependência elevada de stockpile (17% vs 12% plano), concentração de produção em frentes limitadas e horas de paragem +29% vs plano.`
                  : `${data.fragilidades.length} fragility signals were identified in the period: high stockpile dependency (17% vs 12% plan), production concentration in limited fronts and downtime hours +29% vs plan.`}
              </p>
            </div>
          )}
        </div>
      </ExecutiveSummarySection>

      {/* ============ BLOCO 1: SINAIS DE ENTREGA VS PLANO ============ */}
      {is2026 && data.sinaisEntrega?.monthly && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <Target className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Sinais de Entrega vs Plano' : 'Delivery Signals vs Plan'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Estamos a entregar o plano com consistência ou apenas parcialmente?' : 'Are we delivering the plan consistently or just partially?'}</p>
            </div>
          </div>
          
          {/* Two side-by-side charts */}
          <div className="grid grid-cols-2 gap-5">
            {/* Quilates Recuperados */}
            <div>
              <h4 className="font-semibold text-slate-700 text-xs mb-2">{language === 'pt' ? 'Quilates Recuperados' : 'Recovered Carats'}</h4>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.sinaisEntrega.monthly} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      tickFormatter={(v) => `${(v/1000).toFixed(1)}K`}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: '#C5A059' }}
                      formatter={(value) => [`${value.toLocaleString()} cts`, '']}
                    />
                    <Bar dataKey="quilatesReal" name={language === 'pt' ? 'Real' : 'Actual'} fill="#10b981" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="quilatesOrc" name={language === 'pt' ? 'Orçamento' : 'Budget'} fill="#10b98140" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-100 flex justify-between items-center">
                <span className="text-[10px] text-slate-500 font-medium">YTD:</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-700">{data.sinaisEntrega.ytd.quilates.real.toLocaleString()} cts</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                    {data.sinaisEntrega.ytd.quilates.variance}%
                  </span>
                </div>
              </div>
            </div>

            {/* Volume DMS */}
            <div>
              <h4 className="font-semibold text-slate-700 text-xs mb-2">{language === 'pt' ? 'Volume DMS' : 'DMS Volume'}</h4>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.sinaisEntrega.monthly} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      label={{ value: 'K m³', angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#94a3b8' } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: '#C5A059' }}
                      formatter={(value) => [`${value} K m³`, '']}
                    />
                    <Bar dataKey="dmsReal" name={language === 'pt' ? 'Real' : 'Actual'} fill="#3b82f6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="dmsOrc" name={language === 'pt' ? 'Orçamento' : 'Budget'} fill="#3b82f640" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-100 flex justify-between items-center">
                <span className="text-[10px] text-slate-500 font-medium">YTD:</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-700">{data.sinaisEntrega.ytd.dms.real} K m³</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                    {data.sinaisEntrega.ytd.dms.variance}%
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-[10px] text-slate-400 mt-4 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção e Metalurgia' : 'Source: Monthly Production and Metallurgy Reports'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 2: DESVIOS OPERACIONAIS COM MAIOR MATERIALIDADE ============ */}
      {is2026 && data.desviosMateriais && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Desvios Operacionais com Maior Materialidade' : 'Material Operational Deviations'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Quais são os desvios que realmente importam para a entrega do plano?' : 'Which deviations really matter for plan delivery?'}</p>
            </div>
          </div>
          
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Tema' : 'Theme'}</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-16">{language === 'pt' ? 'Desvio' : 'Deviation'}</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Evidência' : 'Evidence'}</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Implicação' : 'Implication'}</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-20">{language === 'pt' ? 'Material.' : 'Material.'}</th>
                </tr>
              </thead>
              <tbody>
                {data.desviosMateriais.map((item, idx) => (
                  <tr key={idx} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/30' : ''}`}>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-700 font-medium">{item.tema[language]}</span>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <span className={`font-bold ${item.desvio.startsWith('-') ? 'text-rose-600' : item.desvio.startsWith('+') && parseFloat(item.desvio) > 10 ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {item.desvio}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-600">{item.evidencia[language]}</span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-600">{item.implicacao[language]}</span>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <SeverityBadge severity={item.materialidade} language={language} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 mt-2.5 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção, Metalurgia e Planeamento' : 'Source: Monthly Production, Metallurgy and Planning Reports'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 3: FRAGILIDADE DA ENTREGA / DEPENDÊNCIAS CRÍTICAS ============ */}
      {is2026 && data.fragilidades && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Fragilidade da Entrega / Dependências Críticas' : 'Delivery Fragility / Critical Dependencies'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'O plano está a ser entregue com robustez ou com dependências frágeis?' : 'Is the plan being delivered with robustness or fragile dependencies?'}</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {data.fragilidades.map((item, idx) => (
              <div key={idx} className={`p-3 rounded-lg border ${
                item.risco === 'high' ? 'bg-rose-50 border-rose-200' :
                item.risco === 'medium' ? 'bg-amber-50 border-amber-200' :
                'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      item.risco === 'high' ? 'bg-rose-500' :
                      item.risco === 'medium' ? 'bg-amber-500' :
                      'bg-slate-400'
                    }`}></span>
                    <h4 className="font-semibold text-slate-800 text-xs">{item.tipo[language]}</h4>
                  </div>
                  <SeverityBadge severity={item.risco} language={language} />
                </div>
                <div className="grid grid-cols-2 gap-4 text-[11px]">
                  <div>
                    <span className="text-slate-500 font-medium">{language === 'pt' ? 'Sinal:' : 'Signal:'}</span>
                    <p className="text-slate-700 mt-0.5">{item.sinal[language]}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">{language === 'pt' ? 'Implicação:' : 'Implication:'}</span>
                    <p className="text-slate-700 mt-0.5">{item.implicacao[language]}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção e reuniões de acompanhamento operacional' : 'Source: Monthly Production Reports and operational follow-up meetings'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 4: WATCHLIST DE RISCO OPERACIONAL ============ */}
      {is2026 && data.watchlist && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center">
              <Eye className="w-4 h-4 text-rose-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Watchlist de Risco Operacional' : 'Operational Risk Watchlist'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'O que exige maior acompanhamento de gestão no curto prazo?' : 'What requires closer management attention in the short term?'}</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {data.watchlist.map((item, idx) => (
              <div key={idx} className={`p-4 rounded-lg border ${
                item.severidade === 'high' ? 'bg-rose-50/50 border-rose-200' :
                item.severidade === 'medium' ? 'bg-amber-50/50 border-amber-200' :
                'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-start justify-between mb-3">
                  <h4 className={`font-semibold text-sm ${
                    item.severidade === 'high' ? 'text-rose-700' :
                    item.severidade === 'medium' ? 'text-amber-700' :
                    'text-slate-700'
                  }`}>
                    {item.risco[language]}
                  </h4>
                  <SeverityBadge severity={item.severidade} language={language} />
                </div>
                
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
                  <div>
                    <span className="text-slate-500 font-medium">{language === 'pt' ? 'Evidência:' : 'Evidence:'}</span>
                    <p className="text-slate-700">{item.evidencia[language]}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">{language === 'pt' ? 'Implicação Operacional:' : 'Operational Implication:'}</span>
                    <p className="text-slate-700">{item.implicacaoOperacional[language]}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">{language === 'pt' ? 'Leitura Financeira:' : 'Financial Reading:'}</span>
                    <p className="text-slate-700">{item.implicacaoFinanceira[language]}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 font-medium">{language === 'pt' ? 'Mitigação:' : 'Mitigation:'}</span>
                    <p className="text-slate-700">{item.mitigacao[language]}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Análise integrada de Relatórios Mensais e POA' : 'Source: Integrated analysis of Monthly Reports and POA'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 5: PONTE OPERAÇÃO → LEITURA EXECUTIVA DE RISCO (qualitativa) ============ */}
      <div className="bg-gradient-to-br from-[#0A192F] to-[#112240] rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gold-500/20 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-gold-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{language === 'pt' ? 'Ponte Operação → Leitura Executiva de Risco' : 'Operations → Executive Risk Reading Bridge'}</h3>
            <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Tradução qualitativa dos factos operacionais em risco sobre execução' : 'Qualitative translation of operational facts to execution risk'}</p>
          </div>
        </div>
        
        <div className="overflow-hidden rounded-lg border border-white/10">
          <table className="w-full">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Facto Operacional' : 'Operational Fact'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Implicação Operacional' : 'Operational Implication'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Leitura Executiva de Risco' : 'Executive Risk Reading'}</th>
                <th className="text-center py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-20">{language === 'pt' ? 'Risco' : 'Risk'}</th>
              </tr>
            </thead>
            <tbody>
              {data.ponteRisco.map((item, idx) => (
                <tr key={idx} className={`border-t border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs font-medium ${
                      item.severidade === 'low' ? 'text-emerald-400' :
                      item.severidade === 'medium' ? 'text-amber-400' :
                      'text-rose-400'
                    }`}>
                      {item.facto[language]}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.implicacaoOperacional[language]}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.leituraRisco[language]}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      item.severidade === 'low' ? 'bg-emerald-500/20 text-emerald-400' :
                      item.severidade === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-rose-500/20 text-rose-400'
                    }`}>
                      {item.severidade === 'low' ? (language === 'pt' ? 'Baixo' : 'Low') :
                       item.severidade === 'medium' ? (language === 'pt' ? 'Médio' : 'Medium') :
                       (language === 'pt' ? 'Alto' : 'High')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 mt-3 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {language === 'pt' ? 'Nota: Leitura qualitativa — quantificação financeira requer modelo dedicado e análise específica' : 'Note: Qualitative reading — financial quantification requires dedicated model and specific analysis'}
        </p>
      </div>
    </div>
  );
};

export default RiskDelivery;
